import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Calendar, BarChart3, TrendingUp, Clock, RefreshCw, Download, Zap, Sun, Wind, Thermometer } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { useToast } from '../../hooks/use-toast';
import apiService from '../../services/api';
import { API_CONFIG } from '../../config/api';
import moment from 'moment';
import { todayISO } from '../../lib/utils';

const PowerForecast = ({ type }) => {
  const [forecastData, setForecastData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const todayStr = todayISO();
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [selectedMonth, setSelectedMonth] = useState(todayStr.substring(0,7));
  const todayDate = moment().format('YYYY-MM-DD');
  const [startDate, setStartDate] = useState(moment().subtract(4, 'days').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(todayDate);
  const [currentDateTime, setCurrentDateTime] = useState(moment().format('DD/MM/YYYY HH:mm:ss'));
  
  // Cumulative data states
  const [powerForecastConfig, setPowerForecastConfig] = useState({
    power_cumulative_actual: { name: 'Cumulative Actual (kWh)', value: '-' },
    power_cumulative_forecasted: { name: 'Cumulative Forecasted (kWh)', value: '-' },
    power_cumulative_deviation: { name: 'Cumulative Deviation (%)', value: '-' },
    power_RMSE: { name: 'RMSE', value: '-' },
    power_MAE: { name: 'MAE', value: '-' },
    power_MAPE: { name: 'MAPE (%)', value: '-' },
  });
  
  const [weatherForecastConfig, setWeatherForecastConfig] = useState({
    irradiance_cumulative_actual: { name: 'Cumulative Actual (kWh/m²)', value: '-' },
    irradiance_cumulative_forecasted: { name: 'Cumulative Forecasted (kWh/m²)', value: '-' },
    irradiance_cumulative_deviation: { name: 'Cumulative Deviation (%)', value: '-' },
    wind_speed_avg: { name: 'Avg Wind Speed (m/s)', value: '-' },
    ambient_temp_avg: { name: 'Avg Ambient Temp (°C)', value: '-' },
  });

  const { toast } = useToast();

  // Auto-refresh for intra-day (every 60 seconds)
  useEffect(() => {
    fetchForecastData();
    
    let timer;
    if (type === 'intra') {
      timer = setInterval(() => {
        setCurrentDateTime(moment().format('DD/MM/YYYY HH:mm:ss'));
        fetchForecastData();
      }, 60000);
    }
    
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [type, selectedDate, selectedMonth, startDate, endDate]);

  const fetchForecastData = async () => {
    try {
      setIsLoading(true);
      
      let response;
      switch (type) {
        case 'intra':
          response = await apiService.getIntraDayForecast({
            date: selectedDate,
            plant_id: API_CONFIG.PLANT_ID
          });
          break;
        case 'inter':
          response = await apiService.getInterDayForecast({
            date: selectedDate,
            plant_id: API_CONFIG.PLANT_ID
          });
          break;
        case 'week':
          response = await apiService.getWeekAheadForecast({
            date: selectedDate,
            plant_id: API_CONFIG.PLANT_ID
          });
          break;
        case 'month': {
          const monthStr = moment(selectedMonth+'-01').format('MMM').toLowerCase(); // e.g., "aug"
          response = await apiService.getMonthAheadForecast({
            month: monthStr,
            plant_id: API_CONFIG.PLANT_ID
          });
          break;
        }
        case 'historical':
          response = await apiService.getHistoricalForecast({
            start_date: startDate + ' 00:00:00',
            end_date: endDate + ' 23:59:59',
            plant_id: API_CONFIG.PLANT_ID
          });
          break;
        default:
          return;
      }

      if (response.data && response.data.json_object) {
        const data = response.data.json_object;
        setForecastData(data);
        
        // Update cumulative values
        if (data.cumulative) {
          updateCumulativeValues(data.cumulative);
        }
      }
    } catch (error) {
      console.error('Error fetching forecast data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch forecast data. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateCumulativeValues = (cumulative) => {
    const getVal = (val) => val !== undefined && val !== null ? Number(val).toFixed(2) : '-';

    // Map backend -> UI fields
    setPowerForecastConfig(prev => ({
      ...prev,
      power_cumulative_actual:      { ...prev.power_cumulative_actual,      name: type==='month' ? 'Energy Export (MWh)' : 'Cumulative Actual (kWh)',      value: getVal(cumulative.energy_export_actual ?? cumulative.energy_export ?? cumulative.energy_actual) },
      power_cumulative_forecasted:  { ...prev.power_cumulative_forecasted,  name: type==='month' ? 'Energy Forecasted (MWh)' : 'Cumulative Forecasted (kWh)',  value: getVal(cumulative.energy_export_forecasted ?? cumulative.energy_forecasted) },
      power_cumulative_deviation:   { ...prev.power_cumulative_deviation,   value: getVal(cumulative.energy_deviation) },
      power_RMSE:                  { ...prev.power_RMSE,                  value: getVal(cumulative.RMSE) },
      power_MAE:                   { ...prev.power_MAE,                   value: getVal(cumulative.MAE) },
      power_MAPE:                  { ...prev.power_MAPE,                  value: getVal(cumulative.MAPE) },
    }));

    setWeatherForecastConfig(prev => ({
      ...prev,
      irradiance_cumulative_actual:     { ...prev.irradiance_cumulative_actual,     value: getVal(cumulative.irradiation_actual) },
      irradiance_cumulative_forecasted: { ...prev.irradiance_cumulative_forecasted, value: getVal(cumulative.irradiation_forecasted) },
      irradiance_cumulative_deviation:  { ...prev.irradiance_cumulative_deviation,  value: getVal(cumulative.irradiation_deviation) },
      wind_speed_avg:                   { ...prev.wind_speed_avg,                   value: getVal(cumulative.wind_speed) },
      ambient_temp_avg:                 { ...prev.ambient_temp_avg,                 value: getVal(cumulative.ambient_temp) },
    }));
  };

  // Build chart points for param section (active_power_json / irradiance_json)
  const processChartData = (jsonData, param) => {
    if (!jsonData || !jsonData[param]) return [];

    const section = jsonData[param];
    const baseKey = param.startsWith('active') ? 'active_power' : 'Irradiance';
    const actualKey = `${baseKey}_actual`;
    const forecastKey = `${baseKey}_forecasted`;

    return Object.keys(section).sort().map(ts => {
      const item = section[ts] || {};
      const point = {
        time: moment(ts).format('HH:mm'),
        timestamp: ts,
        actual: item[actualKey] ?? 0,
        forecasted: item[forecastKey] ?? 0,
      };
      if(param==='irradiance_json'){
        point.wind = item.wind_speed ?? 0;
      }
      return point;
    });
  };

  const processTableData = (jsonData) => {
    if (!jsonData) return [];
    
    const tableData = [];
    const dateTimeKeys = jsonData.active_power_json ? Object.keys(jsonData.active_power_json).sort() : [];
    
    dateTimeKeys.forEach(key => {
      const powerData = jsonData.active_power_json?.[key] || {};
      const irradianceData = jsonData.irradiance_json?.[key] || {};
      const weatherData = jsonData.irradiance_json?.[key] || {};
      
      tableData.push({
        time: moment(key).format('HH:mm'),
        active_power_actual: (powerData.active_power_actual ?? powerData.actual ?? null) !== null ? (powerData.active_power_actual ?? powerData.actual).toFixed(2) : '-',
        active_power_forecasted: (powerData.active_power_forecasted ?? powerData.forecasted ?? null) !== null ? (powerData.active_power_forecasted ?? powerData.forecasted).toFixed(2) : '-',
        power_deviation: powerData.deviation?.toFixed(2) || '-',
        Irradiance_actual: (irradianceData.Irradiance_actual ?? irradianceData.actual ?? null) !== null ? (irradianceData.Irradiance_actual ?? irradianceData.actual).toFixed(2) : '-',
        Irradiance_forecasted: (irradianceData.Irradiance_forecasted ?? irradianceData.forecasted ?? null) !== null ? (irradianceData.Irradiance_forecasted ?? irradianceData.forecasted).toFixed(2) : '-',
        irradiance_deviation: irradianceData.deviation?.toFixed(2) || '-',
        wind_speed: weatherData.wind_speed?.toFixed(2) || '-',
        ambient_temp: weatherData.ambient_temp?.toFixed(2) || '-',
      });
    });
    
    return tableData;
  };

  // Historical table: include date + time, same columns as intra but with datetime
  const processHistoricalTableData = (jsonData) => {
    if (!jsonData) return [];
    const rows = [];
    const keys = jsonData.active_power_json ? Object.keys(jsonData.active_power_json).sort() : [];
    keys.forEach(key => {
      const powerData = jsonData.active_power_json?.[key] || {};
      const irrData = jsonData.irradiance_json?.[key] || {};
      rows.push({
        datetime: moment(key).format('DD MMM YYYY HH:mm'),
        active_power_actual: powerData.active_power_actual !== undefined && powerData.active_power_actual !== null ? Number(powerData.active_power_actual).toFixed(2) : '-',
        active_power_forecasted: powerData.active_power_forecasted !== undefined && powerData.active_power_forecasted !== null ? Number(powerData.active_power_forecasted).toFixed(2) : '-',
        power_deviation: powerData.deviation !== undefined && powerData.deviation !== null ? Number(powerData.deviation).toFixed(2) : '-',
        irr_actual: (irrData.Irradiance_actual ?? irrData.actual ?? null) !== null ? Number(irrData.Irradiance_actual ?? irrData.actual).toFixed(2) : '-',
        irr_forecasted: (irrData.Irradiance_forecasted ?? irrData.forecasted ?? null) !== null ? Number(irrData.Irradiance_forecasted ?? irrData.forecasted).toFixed(2) : '-',
        irr_deviation: irrData.deviation !== undefined && irrData.deviation !== null ? Number(irrData.deviation).toFixed(2) : '-',
      });
    });
    return rows;
  };

  // Table for inter-day: only forecast columns
  const processInterTableData = (jsonData) => {
    if (!jsonData) return [];
    const table = [];
    const keys = jsonData.active_power_json ? Object.keys(jsonData.active_power_json).sort() : [];
    keys.forEach(ts => {
      const p = jsonData.active_power_json?.[ts] || {};
      const irr = jsonData.irradiance_json?.[ts];
      table.push({
        time: moment(ts).format('DD MMM HH:mm'),
        power_forecasted: (p.active_power_forecasted ?? p.power_forecasted ?? 0).toFixed(2),
        irr_forecasted: (
          (irr?.Irradiance_forecasted) ??
          (irr?.irradiance_forecasted) ??
          (irr?.GHI_Irradiance_Forecasted) ??
          (irr?.ghi_irradiance_forecasted) ??
          (p.Irradiance_forecasted ?? p.irradiance_forecasted ?? 0)
        ).toFixed(2),
        wind_speed: (irr?.wind_speed ?? 0).toFixed(2)
      });
    });
    return table;
  };

  // Build combined dataset for week-ahead chart
  const processWeekChartData = (jsonData) => {
    if(!jsonData) return [];
    const keys = jsonData.active_power_json ? Object.keys(jsonData.active_power_json).sort() : [];
    return keys.map(ts=>{
      const power = jsonData.active_power_json?.[ts] || {};
      const irr = jsonData.irradiance_json?.[ts] || {};
      return {
        time: moment(ts).format('DD MMM'),
        power: (power.active_power_forecasted ?? power.power_forecasted ?? 0),
        irr: Number(
          (irr?.Irradiance_forecasted) ??
          (irr?.irradiance_forecasted) ??
          (irr?.GHI_Irradiance_Forecasted) ??
          (irr?.ghi_irradiance_forecasted) ??
          (power.Irradiance_forecasted ?? power.irradiance_forecasted ?? 0)
        )
      };
    });
  };

  const processWeekTableData = (jsonData) => {
    if(!jsonData) return [];
    const keys = jsonData.active_power_json ? Object.keys(jsonData.active_power_json).sort() : [];
    return keys.map(ts=>{
      const power = jsonData.active_power_json?.[ts] || {};
      const irr = jsonData.irradiance_json?.[ts];
      return {
        time: moment(ts).format('DD MMM'),
        power_forecasted: (power.active_power_forecasted ?? power.power_forecasted ?? 0).toFixed(2),
        irr_forecasted: Number(
          (irr?.Irradiance_forecasted) ??
          (irr?.irradiance_forecasted) ??
          (irr?.GHI_Irradiance_Forecasted) ??
          (irr?.ghi_irradiance_forecasted) ??
          (power.Irradiance_forecasted ?? power.irradiance_forecasted ?? 0)
        ).toFixed(2)
      };
    });
  };

  /* ---------------------- MONTH-AHEAD HELPERS --------------------- */
  const processMonthChartData = (jsonData) => {
    if (!jsonData) return [];

    const dayMap = {};

    // Prefer daily aggregated energy numbers if present
    if (jsonData.active_power_json) {
      Object.entries(jsonData.active_power_json).forEach(([ts, val]) => {
        const dayISO = ts.split(' ')[0]; // YYYY-MM-DD
        if (!dayMap[dayISO]) {
          dayMap[dayISO] = {
            day: moment(dayISO).format('DD MMM'),
            actual: null,
            forecasted: null
          };
        }

        if (val.energy_export_actual !== undefined && val.energy_export_actual !== null) {
          dayMap[dayISO].actual = Number(val.energy_export_actual);
        }
        if (val.energy_export_forecasted !== undefined && val.energy_export_forecasted !== null) {
          dayMap[dayISO].forecasted = Number(val.energy_export_forecasted);
        }
      });
    }

    // Fallback – if only hourly MW is provided, accumulate to MWh (not expected in current API)
    if (!jsonData.active_power_json && jsonData.active_power_hourly_json) {
      Object.entries(jsonData.active_power_hourly_json).forEach(([ts, val]) => {
        const dayISO = ts.split(' ')[0];
        if (!dayMap[dayISO]) {
          dayMap[dayISO] = {
            day: moment(dayISO).format('DD MMM'),
            actual: null,
            forecasted: 0
          };
        }
        const mw = Number(val.active_power_forecasted || 0);
        dayMap[dayISO].forecasted += mw * 0.25; // 15-min interval → 0.25 h
      });
    }

    return Object.entries(dayMap)
      .sort(([d1], [d2]) => new Date(d1) - new Date(d2))
      .map(([, obj]) => obj);
  };

  const processMonthTableData = (jsonData) => {
    if (!jsonData) return [];
    const rows = [];

    if (jsonData.active_power_json) {
      Object.entries(jsonData.active_power_json)
        .sort(([d1], [d2]) => new Date(d1) - new Date(d2))
        .forEach(([ts, val]) => {
          const dayISO = ts.split(' ')[0];
          rows.push({
            day: moment(dayISO).format('DD MMM'),
            energy_export_actual: val.energy_export_actual !== null && val.energy_export_actual !== undefined ? Number(val.energy_export_actual).toFixed(2) : '-',
            energy_export_forecasted: val.energy_export_forecasted !== null && val.energy_export_forecasted !== undefined ? Number(val.energy_export_forecasted).toFixed(2) : '-',
          });
        });
    }

    return rows;
  };

  const getForecastTitle = () => {
    switch (type) {
      case 'intra':
        return 'Intra-Day Forecast';
      case 'inter':
        return 'Inter-Day Forecast';
      case 'week':
        return 'Week-Ahead Forecast';
      case 'month':
        return 'Month-Ahead Forecast';
      case 'historical':
        return 'Historical Forecast';
      default:
        return 'Power Forecast';
    }
  };

  const handleExport = () => {
    const tableData = processTableData(forecastData);
    if (tableData.length === 0) {
      toast({
        title: 'No data',
        description: 'There is no data to export yet.'
      });
      return;
    }
    
    const headers = ['Time', 'Active Power Actual', 'Active Power Forecasted', 'Power Deviation', 
                    'Irradiance Actual', 'Irradiance Forecasted', 'Irradiance Deviation', 
                    'Wind Speed', 'Ambient Temp'];
    const csvRows = [headers.join(',')];
    
    tableData.forEach(row => {
      csvRows.push([
        row.time,
        row.active_power_actual,
        row.active_power_forecasted,
        row.power_deviation,
        row.Irradiance_actual,
        row.Irradiance_forecasted,
        row.irradiance_deviation,
        row.wind_speed,
        row.ambient_temp
      ].join(','));
    });
    
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `power-forecast-${type}-${moment().format('YYYY-MM-DD')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    
    toast({
      title: 'Success',
      description: 'Data exported successfully.'
    });
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{getForecastTitle()}</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Last updated: {currentDateTime}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          {type === 'intra' && (
            <div className="flex items-center space-x-2 bg-green-100 dark:bg-green-900/20 px-3 py-1 rounded-full">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-green-800 dark:text-green-200">LIVE</span>
            </div>
          )}
          <Button variant="outline" onClick={handleExport} disabled={!forecastData}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button onClick={fetchForecastData} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Date Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            <span>Date Selection</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {type === 'month' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Month
                </label>
                <Input 
                  type="month" 
                  value={selectedMonth} 
                  onChange={e => setSelectedMonth(e.target.value)} 
                  className="w-full" 
                />
              </div>
            ) : type === 'historical' ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Start Date
                  </label>
                  <Input 
                    type="date" 
                    value={startDate} 
                    onChange={e => setStartDate(e.target.value)} 
                    className="w-full" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    End Date
                  </label>
                  <Input 
                    type="date" 
                    value={endDate} 
                    onChange={e => setEndDate(e.target.value)} 
                    className="w-full" 
                  />
                </div>
              </>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Date
                </label>
                <Input 
                  type="date" 
                  value={selectedDate} 
                  onChange={e => setSelectedDate(e.target.value)} 
                  className="w-full" 
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Combined Metrics Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5 text-blue-500" />
            <span>Forecast Summary</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {[...Object.values(powerForecastConfig), ...Object.values(weatherForecastConfig)]
              .filter(cfg => cfg.value !== '-' && cfg.value !== undefined)
              .map(cfg => (
                <div key={cfg.name}>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{cfg.name}</p>
                  <p className="text-lg font-semibold">{cfg.value}</p>
                </div>
              ))}
          </div>
        </CardContent>
      </Card>

      {/* Charts */}
      {forecastData && (
        <div className={`grid grid-cols-1 ${['intra','inter','historical'].includes(type) ? 'lg:grid-cols-2' : ''} gap-6`}>
          {(['intra','inter','historical'].includes(type)) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Zap className="h-5 w-5" />
                <span>Active Power (kW)</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={processChartData(forecastData, 'active_power_json')}>
                    <XAxis dataKey="time" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="actual" stroke="#6366F1" strokeWidth={2} name="Actual" dot={false} />
                    <Line type="monotone" dataKey="forecasted" stroke="#22C55E" strokeWidth={2} name="Forecasted" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          )}

          {/* Irradiance chart only for intra / inter forecasts */}
          {(['intra','inter','historical'].includes(type)) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Sun className="h-5 w-5" />
                  <span>Irradiance (W/m²)</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={processChartData(forecastData, 'irradiance_json')}>
                      <XAxis dataKey="time" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip />
                      <Legend />
                      <Line yAxisId="left" type="monotone" dataKey="actual" stroke="#8B5CF6" strokeWidth={2} name="Actual" dot={false} />
                      <Line yAxisId="left" type="monotone" dataKey="forecasted" stroke="#FACC15" strokeWidth={2} name="Forecasted" dot={false} />
                      {type==='inter' && (
                        <Line yAxisId="right" type="monotone" dataKey="wind" stroke="#14B8A6" strokeWidth={2} name="Wind Speed (m/s)" dot={false} />
                      )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          )}

          {type==='week' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <BarChart3 className="h-5 w-5" />
                  <span>Weekly Forecast</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={processWeekChartData(forecastData)}>
                      <XAxis dataKey="time" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip />
                      <Legend />
                      <Line yAxisId="left" type="monotone" dataKey="power" name="Power (MW)" stroke="#6366F1" strokeWidth={2} dot={false} />
                      <Line yAxisId="right" type="monotone" dataKey="irr" name="Irradiance (W/m²)" stroke="#FACC15" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Month-ahead chart */}
          {type==='month' && forecastData && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <BarChart3 className="h-5 w-5" />
                  <span>Daily Energy Export vs Forecast (MWh)</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={processMonthChartData(forecastData)}>
                      <XAxis dataKey="day" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="actual" name="Energy Export" fill="#6366F1" />
                      <Bar dataKey="forecasted" name="Energy Forecasted" fill="#22C55E" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Intra-day data table */}
      {type === 'intra' && forecastData && (
        <Card>
          <CardHeader>
            <CardTitle>Forecast Data Table</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-96 overflow-y-auto overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-2 px-2 font-medium">Time</th>
                    <th className="text-center py-2 px-2 font-medium" colSpan="3">Active Power</th>
                    <th className="text-center py-2 px-2 font-medium" colSpan="3">Irradiance</th>
                    <th className="text-center py-2 px-2 font-medium" colSpan="2">Weather</th>
                  </tr>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-2 px-2 font-medium text-xs"></th>
                    <th className="text-right py-2 px-2 font-medium text-xs">Actual</th>
                    <th className="text-right py-2 px-2 font-medium text-xs">Forecasted</th>
                    <th className="text-right py-2 px-2 font-medium text-xs">Deviation</th>
                    <th className="text-right py-2 px-2 font-medium text-xs">Actual</th>
                    <th className="text-right py-2 px-2 font-medium text-xs">Forecasted</th>
                    <th className="text-right py-2 px-2 font-medium text-xs">Deviation</th>
                    <th className="text-right py-2 px-2 font-medium text-xs">Wind Speed</th>
                    <th className="text-right py-2 px-2 font-medium text-xs">Ambient Temp</th>
                  </tr>
                </thead>
                <tbody>
                  {processTableData(forecastData).map((row, index) => (
                    <tr key={index} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-2 px-2">{row.time}</td>
                      <td className="py-2 px-2 text-right">{row.active_power_actual}</td>
                      <td className="py-2 px-2 text-right">{row.active_power_forecasted}</td>
                      <td className="py-2 px-2 text-right">{row.power_deviation}</td>
                      <td className="py-2 px-2 text-right">{row.Irradiance_actual}</td>
                      <td className="py-2 px-2 text-right">{row.Irradiance_forecasted}</td>
                      <td className="py-2 px-2 text-right">{row.irradiance_deviation}</td>
                      <td className="py-2 px-2 text-right">{row.wind_speed}</td>
                      <td className="py-2 px-2 text-right">{row.ambient_temp}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Inter-day data table */}
      {type==='inter' && forecastData && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Forecast Data Table</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-2 px-2 font-medium">Time</th>
                    <th className="text-right py-2 px-2 font-medium">Active Power Forecasted (MW)</th>
                    <th className="text-right py-2 px-2 font-medium">GHI Irradiance Forecasted (W/m²)</th>
                    <th className="text-right py-2 px-2 font-medium">Wind Speed (m/s)</th>
                  </tr>
                </thead>
                <tbody>
                  {processInterTableData(forecastData).map((row,idx)=>(
                    <tr key={idx} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-2 px-2">{row.time}</td>
                      <td className="py-2 px-2 text-right">{row.power_forecasted}</td>
                      <td className="py-2 px-2 text-right">{row.irr_forecasted}</td>
                      <td className="py-2 px-2 text-right">{row.wind_speed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Week-ahead table */}
      {type==='week' && forecastData && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Forecast Data Table</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-2 px-2 font-medium">Time</th>
                    <th className="text-right py-2 px-2 font-medium">Active Power Forecasted (MW)</th>
                    <th className="text-right py-2 px-2 font-medium">GHI Irradiance Forecasted (W/m²)</th>
                  </tr>
                </thead>
                <tbody>
                  {processWeekTableData(forecastData).map((r,i)=>(
                    <tr key={i} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-2 px-2">{r.time}</td>
                      <td className="py-2 px-2 text-right">{r.power_forecasted}</td>
                      <td className="py-2 px-2 text-right">{r.irr_forecasted}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Historical table */}
      {type==='historical' && forecastData && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Forecast Data Table</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-96 overflow-y-auto overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-2 px-2 font-medium">Date & Time</th>
                    <th className="text-right py-2 px-2 font-medium" colSpan="3">Active Power</th>
                    <th className="text-right py-2 px-2 font-medium" colSpan="3">GHI Irradiance</th>
                  </tr>
                  <tr className="border-b border-gray-200 dark:border-gray-700 text-xs">
                    <th></th>
                    <th className="text-right py-1 px-2 font-medium">Actual (MW)</th>
                    <th className="text-right py-1 px-2 font-medium">Forecasted (MW)</th>
                    <th className="text-right py-1 px-2 font-medium">Deviation (%)</th>
                    <th className="text-right py-1 px-2 font-medium">Actual (W/m²)</th>
                    <th className="text-right py-1 px-2 font-medium">Forecasted (W/m²)</th>
                    <th className="text-right py-1 px-2 font-medium">Deviation (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {processHistoricalTableData(forecastData).map((row,i)=>(
                    <tr key={i} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-1 px-2">{row.datetime}</td>
                      <td className="py-1 px-2 text-right">{row.active_power_actual}</td>
                      <td className="py-1 px-2 text-right">{row.active_power_forecasted}</td>
                      <td className="py-1 px-2 text-right">{row.power_deviation}</td>
                      <td className="py-1 px-2 text-right">{row.irr_actual}</td>
                      <td className="py-1 px-2 text-right">{row.irr_forecasted}</td>
                      <td className="py-1 px-2 text-right">{row.irr_deviation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Month table */}
      {type==='month' && forecastData && (
        <Card className="mt-6">
          <CardHeader><CardTitle>Forecast Data Table</CardTitle></CardHeader>
          <CardContent>
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-2 px-2 font-medium">Day</th>
                    <th className="text-right py-2 px-2 font-medium">Energy Export (MWh)</th>
                    <th className="text-right py-2 px-2 font-medium">Energy Forecasted (MWh)</th>
                  </tr>
                </thead>
                <tbody>
                  {processMonthTableData(forecastData).map((r,i)=>(
                    <tr key={i} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-2 px-2">{r.day}</td>
                      <td className="py-2 px-2 text-right">{r.energy_export_actual}</td>
                      <td className="py-2 px-2 text-right">{r.energy_export_forecasted}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PowerForecast;